// Valuación de inventario (FIFO/PEPS) — reporte para contabilidad.
// Lee las vistas v_valuacion_inventario, v_cogs_mensual y v_stock_capas.
// Costeo PEPS: cada salida consume el lote más viejo. Valores NETOS (sin IVA).

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmt0 = (n) => Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 2 })
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('es-UY', { dateStyle: 'short' }) : '—'

export default function ValuacionFifo() {
  const [val, setVal] = useState([])
  const [cogs, setCogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [drawer, setDrawer] = useState(null)
  const [capas, setCapas] = useState([])
  const [capasLoading, setCapasLoading] = useState(false)

  async function cargar() {
    setLoading(true)
    const [{ data: v }, { data: c }] = await Promise.all([
      supabase.from('v_valuacion_inventario').select('*'),
      supabase.from('v_cogs_mensual').select('*').order('mes', { ascending: false }).limit(12),
    ])
    setVal(v || [])
    setCogs(c || [])
    setLoading(false)
  }
  useEffect(() => { cargar() }, [])

  useEffect(() => {
    if (!drawer) { setCapas([]); return }
    let cancel = false
    ;(async () => {
      setCapasLoading(true)
      const { data } = await supabase.from('v_stock_capas').select('*')
        .eq('producto_id', drawer.producto_id).order('fecha', { ascending: true })
      if (!cancel) { setCapas(data || []); setCapasLoading(false) }
    })()
    return () => { cancel = true }
  }, [drawer])

  const stats = useMemo(() => {
    const conStock = val.filter(p => Number(p.unidades) > 0)
    return {
      productos: conStock.length,
      valorNeto: val.reduce((s, p) => s + Number(p.valor_neto || 0), 0),
      valorIva: val.reduce((s, p) => s + Number(p.valor_con_iva || 0), 0),
      cogsMes: cogs[0]?.cogs_neto || 0,
    }
  }, [val, cogs])

  const filas = useMemo(
    () => [...val].filter(p => Number(p.unidades) > 0).sort((a, b) => Number(b.valor_neto) - Number(a.valor_neto)),
    [val]
  )

  return (
    <AdminLayout>
      <div className="page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0 }}>📦 Valuación de inventario (FIFO)</h1>
            <p className="lead" style={{ margin: '4px 0 0' }}>Costeo PEPS: cada salida consume el lote más viejo. Valores netos (sin IVA).</p>
          </div>
          <button onClick={cargar} style={{ background: '#1A3461', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>↻ Refrescar</button>
        </div>

        <div className="cards-grid" style={{ marginTop: 16 }}>
          <div className="card"><h3>Valor inventario (neto)</h3><div className="stat">{fmt(stats.valorNeto)}</div><div className="desc">sin IVA — base contable</div></div>
          <div className="card"><h3>Valor inventario (c/IVA)</h3><div className="stat">{fmt(stats.valorIva)}</div></div>
          <div className="card"><h3>Productos con stock</h3><div className="stat">{stats.productos}</div></div>
          <div className="card" style={{ borderLeft: '4px solid #00A8B5' }}><h3>COGS del mes</h3><div className="stat" style={{ color: '#00A8B5' }}>{fmt(stats.cogsMes)}</div><div className="desc">costo de lo vendido (neto)</div></div>
        </div>

        {cogs.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>COGS por mes (neto)</h3>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              {cogs.map(m => (
                <div key={m.mes} style={{ minWidth: 100 }}>
                  <div style={{ fontSize: 11, color: '#64748B' }}>{fmtDate(m.mes)}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1A3461' }}>{fmt(m.cogs_neto)}</div>
                  <div style={{ fontSize: 10, color: '#94A3B8' }}>{fmt0(m.unidades)} u</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)' }}>Cargando…</div>
        ) : (
          <table className="t-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th style={{ textAlign: 'right' }}>Unidades</th>
                <th style={{ textAlign: 'right' }}>Valor neto</th>
                <th style={{ textAlign: 'right' }}>Valor c/IVA</th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {filas.map(p => (
                <tr key={p.producto_id} style={{ cursor: 'pointer' }} onClick={() => setDrawer(p)}>
                  <td>
                    <div style={{ fontWeight: 700, color: '#1A3461', fontSize: 13 }}>{p.nombre}</div>
                    <code style={{ fontSize: 10.5, color: '#94A3B8' }}>{p.sku}</code>
                  </td>
                  <td style={{ textAlign: 'right' }}>{fmt0(p.unidades)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(p.valor_neto)}</td>
                  <td style={{ textAlign: 'right', color: '#64748B' }}>{fmt(p.valor_con_iva)}</td>
                  <td style={{ textAlign: 'right', color: '#94A3B8', fontSize: 11 }}>ver capas →</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {drawer && (
          <div onClick={() => setDrawer(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: '#fff', overflowY: 'auto', padding: 22 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>Capas FIFO · <code>{drawer.sku}</code></div>
                  <h2 style={{ margin: '4px 0 0', fontSize: 18, color: '#1A3461' }}>{drawer.nombre}</h2>
                </div>
                <button onClick={() => setDrawer(null)} style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94A3B8' }}>×</button>
              </div>
              <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 12px' }}>De arriba (más viejo) hacia abajo. La primera capa es la que sale primero (PEPS).</p>
              {capasLoading ? (
                <div style={{ color: '#94A3B8' }}>Cargando…</div>
              ) : capas.length === 0 ? (
                <div style={{ color: '#94A3B8' }}>Sin capas con saldo.</div>
              ) : (
                <table className="t-table">
                  <thead>
                    <tr>
                      <th>Ingreso</th>
                      <th>Origen</th>
                      <th style={{ textAlign: 'right' }}>Restante / Inicial</th>
                      <th style={{ textAlign: 'right' }}>Costo neto</th>
                      <th style={{ textAlign: 'right' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {capas.map((c, i) => (
                      <tr key={c.id} style={i === 0 ? { background: '#ECFEFF' } : undefined}>
                        <td style={{ fontSize: 12 }}>
                          {fmtDate(c.fecha)}
                          {i === 0 && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#00A8B5' }}>SALE PRIMERO</span>}
                        </td>
                        <td style={{ fontSize: 11, color: '#64748B' }}>{c.origen}</td>
                        <td style={{ textAlign: 'right' }}>{fmt0(c.cantidad_restante)} / {fmt0(c.cantidad_inicial)}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(c.costo_neto)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(c.valor_neto_restante)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
